import React, { useState, memo, useCallback } from 'react';

interface InputProps {
  label: string;
  type?: string;
  name: string;
  id: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string | boolean;
  helperText?: string | null;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  margin?: 'normal' | 'dense' | 'none';
  showPasswordToggle?: boolean;
  labelPosition?: 'top' | 'floating' | 'side' | 'top-left' | 'top-right' | 'top-middle';
}

const Input: React.FC<InputProps> = memo(({
  label,
  type = 'text',
  name,
  id,
  placeholder,
  value,
  onChange,
  onKeyPress,
  error,
  helperText,
  className = '',
  disabled = false,
  fullWidth = false,
  margin = 'normal',
  showPasswordToggle = false,
  labelPosition = 'top'
}) => {
  const [showPassword, setShowPassword] = useState(false);
  
  const handlePasswordToggle = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);
  
  const marginClasses = {
    normal: 'my-4',
    dense: 'my-2',
    none: '',
  };
  
  const hasError = typeof error === 'boolean' ? error : !!error;
  const inputType = type === 'password' && showPassword ? 'text' : type;
  
  const renderTopLabel = () => (
    <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]}`}>
      <label htmlFor={id} className={`label group/label ${className}`}>
        <p className="label-text">{label}:</p>
        <div className="relative">
          <input
            type={inputType}
            className={`input peer ${hasError ? 'border-red-500' : ''} ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}`}
            name={name}
            id={id}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onKeyPress={onKeyPress}
            disabled={disabled}
          />
          {renderPasswordToggle()}
        </div>
        {renderErrorMessage()}
      </label>
      {renderHelperText()}
    </div>
  );
  
  const renderFloatingLabel = () => (
    <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]}`}>
      <div className="relative">
        <input
          type={inputType}
          className={`input peer placeholder-transparent ${hasError ? 'border-red-500' : ''} ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}`}
          name={name}
          id={id}
          placeholder={placeholder || label}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          disabled={disabled}
        />
        <label
          htmlFor={id}
          className={`absolute left-3 transition-all duration-200 pointer-events-none ${
            value || placeholder
              ? '-top-2 text-xs bg-white px-1 text-gray-600'
              : 'top-3 text-base text-gray-500 peer-focus:-top-2 peer-focus:text-xs peer-focus:bg-white peer-focus:px-1 peer-focus:text-gray-600'
          } ${hasError ? 'text-red-500' : ''}`}
        >
          {label}
        </label>
        {renderPasswordToggle()}
      </div>
      {renderErrorMessage()}
      {renderHelperText()}
    </div>
  );
  
  const renderSideLabel = () => (
    <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]} flex items-center gap-3`}>
      <label htmlFor={id} className={`text-sm font-medium text-gray-700 whitespace-nowrap ${hasError ? 'text-red-500' : ''}`}>
        {label}:
      </label>
      <div className="relative flex-1">
        <input
          type={inputType}
          className={`input peer ${hasError ? 'border-red-500' : ''} ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}`}
          name={name}
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          disabled={disabled}
        />
        {renderPasswordToggle()}
      </div>
      {renderErrorMessage()}
      {renderHelperText()}
    </div>
  );
  
  const renderPasswordToggle = () => {
    if (!(type === 'password' && showPasswordToggle)) return null;
    
    return (
      <button
        type="button"
        onClick={handlePasswordToggle}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
        disabled={disabled}
      >
        {showPassword ? (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.88 9.88a3 3 0 104.24 4.24" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.73 5.08A10.43 10.43 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.91 9.91 0 01-1.227 2.01" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6.758 6.758A10.43 10.43 0 002.458 12c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )}
      </button>
    );
  };
  
  const renderErrorMessage = () => {
    if (!(hasError && typeof error === 'string')) return null;
    
    return (
      <p className="input-error">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
            fill="#DF064F"
          />
        </svg>
        {error}
      </p>
    );
  };
  
  const renderHelperText = () => {
    if (!helperText) return null;
    
    return (
      <p className={`mt-1 text-sm ${
        hasError ? 'text-red-500' : 'text-gray-600'
      }`}>
        {helperText}
      </p>
    );
  };
  
  const renderTopLeftLabel = () => (
    <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]}`}>
      <label htmlFor={id} className={`block text-left text-sm font-medium mb-1 ${hasError ? 'text-red-500' : 'text-grey-30 dark:text-white'}`}>
        {label}:
      </label>
      <div className="relative">
        <input
          type={inputType}
          className={`input peer ${hasError ? 'border-red-500' : ''} ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}`}
          name={name}
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          disabled={disabled}
        />
        {renderPasswordToggle()}
      </div>
      {renderErrorMessage()}
      {renderHelperText()}
    </div>
  );
  
  const renderTopRightLabel = () => (
    <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]}`}>
      <label htmlFor={id} className={`block text-right text-sm font-medium mb-1 ${hasError ? 'text-red-500' : 'text-grey-30 dark:text-white'}`}>
        {label}:
      </label>
      <div className="relative">
        <input
          type={inputType}
          className={`input peer ${hasError ? 'border-red-500' : ''} ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}`}
          name={name}
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          disabled={disabled}
        />
        {renderPasswordToggle()}
      </div>
      {renderErrorMessage()}
      {renderHelperText()}
    </div>
  );
  
  const renderTopMiddleLabel = () => (
    <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]}`}>
      <label htmlFor={id} className={`block text-center text-sm font-medium mb-1 ${hasError ? 'text-red-500' : 'text-grey-30 dark:text-white'}`}>
        {label}:
      </label>
      <div className="relative">
        <input
          type={inputType}
          className={`input peer ${hasError ? 'border-red-500' : ''} ${showPasswordToggle && type === 'password' ? 'pr-10' : ''}`}
          name={name}
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          disabled={disabled}
        />
        {renderPasswordToggle()}
      </div>
      {renderErrorMessage()}
      {renderHelperText()}
    </div>
  );
  
  switch (labelPosition) {
    case 'floating':
      return renderFloatingLabel();
    case 'side':
      return renderSideLabel();
    case 'top-left':
      return renderTopLeftLabel();
    case 'top-right':
      return renderTopRightLabel();
    case 'top-middle':
      return renderTopMiddleLabel();
    case 'top':
    default:
      return renderTopLabel();
  }
});

export default Input;